import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import GooeyNav from './GooeyNav';
import { useNavbarScroll } from '../hooks/useNavbarScroll';
import { navItems } from '../config/navConfig';
import { servicesMenuColumns } from '../config/servicesMenuConfig';
import { productCategories } from '../config/productsMenuConfig';
import { useRegion } from '../context/RegionContext';
import { useContactModal } from '../context/ContactModalContext';

// Flatten the service mega-menu columns into a single list of groups for the
// mobile accordion (the columns are only a desktop layout concern).
const serviceGroups = servicesMenuColumns.flat();

// Whether a menu entry should show for the active region.
const inRegion = (entry, region) => !entry.region || entry.region === region;

const RegionToggle = ({ region, toggleRegion }) => (
  <button
    type="button"
    className={`region-toggle ${region}`}
    onClick={toggleRegion}
    aria-label={`Region: ${region.toUpperCase()} — click to switch`}
  >
    <span className="region-knob" />
    <span className="region-label region-label-in">IN</span>
    <span className="region-label region-label-uk">UK</span>
  </button>
);

const PageNavbar = () => {
  const showNavbar = useNavbarScroll();
  const { region, toggleRegion } = useRegion();
  const { openModal } = useContactModal();
  const [menuOpen, setMenuOpen] = useState(false);
  // Label of the mobile sub-menu currently expanded (Services / Products), or null.
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();

  // Close the mobile menu (and any expanded sub-menu) on route change.
  useEffect(() => {
    setMenuOpen(false);
    setExpanded(null);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open, so touch drags scroll the
  // menu (which can be long once a sub-menu is expanded) rather than the page.
  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', menuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [menuOpen]);

  // Sub-items for an expandable nav entry, region-filtered.
  const renderSubmenu = (label) => {
    if (label === 'Services') {
      return serviceGroups.map((grp) => {
        const items = grp.items.filter((it) => !it.hidden && inRegion(it, region));
        if (!items.length) return null;
        return (
          <div className="mobile-subgroup" key={grp.title}>
            <Link to={grp.overview || items[0].href} className="mobile-subgroup-title">
              {grp.title}
            </Link>
            {items.map((it) => (
              <Link to={it.href} className="mobile-sublink" key={it.slug}>
                {it.label}
              </Link>
            ))}
          </div>
        );
      });
    }
    if (label === 'Products') {
      return productCategories.map((cat) => {
        const items = cat.items.filter((it) => inRegion(it, region));
        if (!items.length) return null;
        return (
          <div className="mobile-subgroup" key={cat.title}>
            <span className="mobile-subgroup-title as-label">{cat.title}</span>
            {items.map((it) => (
              <Link to={it.href} className="mobile-sublink" key={it.slug}>
                {it.label}
              </Link>
            ))}
          </div>
        );
      });
    }
    return null;
  };

  return (
    <nav className={`navbar ${showNavbar ? '' : 'hidden'} ${menuOpen ? 'menu-open' : ''}`}>
      <Link to="/" className="logo">
        <img src="/logo.png" alt="Logo Icon" className="logo-img" />
        <img src="/logoname.png" alt="Logo Name" className="logo-name" />
      </Link>

      <GooeyNav items={navItems} />

      <div className="navbar-right">
        <RegionToggle region={region} toggleRegion={toggleRegion} />
        <button type="button" className="navbar-cta" onClick={openModal}>
          Get In Touch
          <span className="btn-arrow">→</span>
        </button>
      </div>

      {/* Mobile hamburger */}
      <button
        type="button"
        className={`navbar-hamburger ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label="Toggle navigation menu"
        aria-expanded={menuOpen}
      >
        <span />
        <span />
        <span />
      </button>

      {/* Mobile dropdown menu */}
      <div className={`navbar-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-links">
          {navItems.map((item) => {
            if (item.megaMenu) {
              const isOpen = expanded === item.label;
              return (
                <li key={item.href} className={`mobile-nav-item ${isOpen ? 'expanded' : ''}`}>
                  <div className="mobile-nav-row">
                    <Link to={item.href} className="mobile-nav-main">{item.label}</Link>
                    <button
                      type="button"
                      className="mobile-nav-toggle"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label} menu`}
                      onClick={() => setExpanded(isOpen ? null : item.label)}
                    >
                      <span className="mobile-chevron" aria-hidden="true">▾</span>
                    </button>
                  </div>
                  {isOpen && <div className="mobile-submenu">{renderSubmenu(item.label)}</div>}
                </li>
              );
            }
            return (
              <li key={item.href}>
                <Link to={item.href}>{item.label}</Link>
              </li>
            );
          })}
        </ul>
        <div className="mobile-menu-actions">
          <RegionToggle region={region} toggleRegion={toggleRegion} />
          <button type="button" className="navbar-cta" onClick={() => { setMenuOpen(false); openModal(); }}>
            Get In Touch
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default PageNavbar;
